import { z } from 'zod'
import { isValidUSPhoneNumber } from 'lib/phone'
import { v4 } from 'uuid'
import { isValid } from 'date-fns'

const shiftRowSchema = z
  .object({
    rowId: z.string().uuid(),
    companyId: z.number().min(1, 'Please select a company'),
    locationName: z
      .string()
      .trim()
      .min(1, 'Please enter a location name')
      .max(40, 'Location name must be 40 characters or less'),
    locationArrivalInstructions: z.string().trim().optional(),
    locationStreet: z
      .string()
      .trim()
      .min(1, 'Please enter a location street')
      .max(50, 'Location street must be 50 characters or less'),
    locationUnit: z.string().trim().optional(),
    locationCity: z
      .string()
      .trim()
      .min(1, 'Please enter a location city')
      .max(30, 'Location city must be 30 characters or less'),
    locationState: z
      .string()
      .trim()
      .min(1, 'Please enter a location state')
      .max(2, 'Location state must be 2 characters or less'),
    locationZip: z
      .string()
      .trim()
      .min(1, 'Please enter a location zip')
      .regex(/^\d{5}$/, 'Location zip must be exactly 5 digits'),
    positionId: z.number().min(1, 'Please select a position'),
    shiftStartDate: z.date().refine((val) => isValid(val), {
      message: 'Please select a start date',
    }),
    shiftEndDate: z.date().refine((val) => isValid(val), {
      message: 'Please select an end date',
    }),
    shiftWorkersNeeded: z.coerce
      .number()
      .min(1, 'Please enter a number of workers needed'),
    shiftLunchLength: z.coerce
      .number()
      .min(0, 'Please enter 0 or a positive number'),
    shiftPayRate: z.coerce.number().optional().nullable(),
    shiftPayLumpSum: z.coerce.number().optional().nullable(),
    shiftBundleGroup: z.string().trim().optional().nullable(),
    shiftSupervisorName: z.string().trim().optional().nullable(),
    shiftSupervisorPhone: z
      .string()
      .trim()
      .optional()
      .nullable()
      .refine(
        (data) => {
          // phone number is optional
          if (!data) {
            return true
          }
          return isValidUSPhoneNumber(data)
        },
        {
          message: 'Please enter a valid US phone number',
          path: ['shiftSupervisorPhone'],
        }
      ),
    workerIds: z.array(z.coerce.number()).optional(),
  })
  .refine(
    (data) => {
      return data.shiftStartDate < data.shiftEndDate
    },
    {
      message: 'Start date must be before end date',
      path: ['shiftStartDate', 'shiftEndDate'],
    }
  )
  .refine(
    (data) => {
      return (
        (!!data.shiftPayLumpSum && !data.shiftPayRate) ||
        (!data.shiftPayLumpSum && !!data.shiftPayRate)
      )
    },
    {
      message: 'Please enter either a pay rate or a pay lump sum',
      path: ['shiftPayRate'],
    }
  )
  .refine(
    (data) => {
      // If shiftSupervisorPhone is provided, shiftSupervisorName is required
      if (data.shiftSupervisorPhone?.trim()?.length) {
        return data.shiftSupervisorName?.trim()?.length
      }
      return true
    },
    {
      message: 'Supervisor name is required if supervisor phone is provided',
      path: ['shiftSupervisorName'],
    }
  )
  .refine(
    (data) => {
      const isPastShift = data.shiftStartDate <= new Date()
      if (isPastShift) {
        return !!data.workerIds?.length
      }
      return true
    },
    {
      message: 'Worker IDs are required for past shifts',
      path: ['workerIds'],
    }
  )
  .refine(
    (data) => {
      const isPastShift = data.shiftStartDate <= new Date()
      if (isPastShift) {
        return data.workerIds?.length === data.shiftWorkersNeeded
      }
      return true
    },
    {
      message: 'Number of worker IDs must match the number of workers needed',
      path: ['workerIds'],
    }
  )
  .refine(
    (data) => {
      const isFutureShift = data.shiftStartDate > new Date()
      if (isFutureShift) {
        return !data.workerIds?.length
      }
      return true
    },
    {
      message: 'Worker IDs are not allowed for future shifts',
      path: ['workerIds'],
    }
  )

export type ShiftRowSchema = z.infer<typeof shiftRowSchema>

export const shiftsTableSchema = z.object({
  shifts: z.array(shiftRowSchema).min(1, 'Please add at least one shift'),
})

export type ShiftsTableSchema = z.infer<typeof shiftsTableSchema>

export function createEmptyShiftRow(): ShiftRowSchema {
  return {
    rowId: v4(),
    companyId: 0,
    locationName: '',
    locationArrivalInstructions: '',
    locationStreet: '',
    locationUnit: '',
    locationCity: '',
    locationState: '',
    locationZip: '',
    positionId: 0,
    shiftStartDate: new Date(),
    shiftEndDate: new Date(),
    shiftWorkersNeeded: 0,
    shiftLunchLength: 0,
    shiftPayRate: null,
    shiftPayLumpSum: null,
    shiftBundleGroup: '',
    shiftSupervisorName: '',
    shiftSupervisorPhone: '',
  }
}
